import type { SxProps, Theme } from '@mui/material';

/** The narrowest a strip tile can be before its value and delta line start to clip. */
export const STRIP_MIN_TILE_WIDTH = 168;
/** Six-tile strips (Growth, product pages) may go a little narrower so they stay one row on a 1280px laptop. */
const SIX_UP_MIN_TILE_WIDTH = 152;

/** Goes on the strip's own box, so the column rules measure the strip and not the page. */
export const STRIP_CONTAINER_SX = { containerType: 'inline-size' } as const;

const below = (px: number) => `@container (max-width: ${px - 1}px)`;
const NARROW = STRIP_MIN_TILE_WIDTH * 3;

/**
 * Grid rules for a strip of `count` tiles: one row while every tile can be STRIP_MIN_TILE_WIDTH wide,
 * then an even wrap with no empty cell (5 tiles wrap 3 over 2). With `hug`, a short strip keeps tiles
 * at a readable width instead of stretching across the page. Cells draw their own hairlines; never
 * paint the grid background, or an empty track shows as a grey block. `minTileWidth` overrides the narrowest a
 * tile goes before the strip wraps, for a strip whose tiles are known to fit narrower.
 */
export function stripGridSx(
  count: number,
  { hug = false, minTileWidth }: { hug?: boolean; minTileWidth?: number } = {},
): SxProps<Theme> {
  const n = Math.max(1, count);
  const oneRow = n * (minTileWidth ?? (n >= 6 ? SIX_UP_MIN_TILE_WIDTH : STRIP_MIN_TILE_WIDTH));
  if (hug) {
    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${n}, minmax(${STRIP_MIN_TILE_WIDTH}px, 240px))`,
      maxWidth: '100%',
      width: 'fit-content',
      [below(oneRow)]: { gridTemplateColumns: '1fr', width: '100%' },
    };
  }
  const wrapped: Record<string, unknown> =
    n === 5
      ? {
          gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
          '& > :nth-of-type(-n+3)': { gridColumn: 'span 2' },
          '& > :nth-of-type(n+4)': { gridColumn: 'span 3' },
        }
      : { gridTemplateColumns: `repeat(${Math.ceil(n / 2)}, minmax(0, 1fr))` };
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
    ...(n > 3 ? { [below(oneRow)]: wrapped } : {}),
    [below(Math.min(oneRow, NARROW))]: {
      gridTemplateColumns: n > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
      '& > :nth-of-type(n)': { gridColumn: 'auto' },
      '& > :last-child:nth-of-type(odd)': { gridColumn: '1 / -1' },
    },
  };
}
